import React from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, ArrowUpRight, ExternalLink } from "lucide-react";
import { PortfolioMeta } from "./PortfolioMeta";
import { PortfolioVisual } from "./PortfolioVisual";
import { findCaseStudy, PORTFOLIO_CASE_STUDIES } from "./portfolioData";

export default function CreativeCaseStudy() {
  const { slug } = useParams();
  const study = findCaseStudy(slug);

  if (!study) {
    return (
      <section className="creative-not-found">
        <PortfolioMeta
          title="Case study not found — Terik Atwell"
          description="This portfolio case study is not available."
          path={`/mycreative/${slug}`}
        />
        <p className="creative-eyebrow">Portfolio</p>
        <h1>That case study isn’t available.</h1>
        <p>The link may have changed. Return to the portfolio to explore the current selected work.</p>
        <Link to="/mycreative" className="creative-primary-link"><ArrowLeft size={18} /> Back to portfolio</Link>
      </section>
    );
  }

  const index = PORTFOLIO_CASE_STUDIES.findIndex((item) => item.slug === study.slug);
  const next = PORTFOLIO_CASE_STUDIES[(index + 1) % PORTFOLIO_CASE_STUDIES.length];

  return (
    <>
      <PortfolioMeta
        title={`${study.title} — Terik Atwell`}
        description={study.summary}
        path={`/mycreative/${study.slug}`}
      />
      <article className="creative-case" style={{ "--project-accent": study.accent }}>
        <div className="creative-case-breadcrumb">
          <Link to="/mycreative"><ArrowLeft size={16} /> Portfolio</Link><span>/</span><span>{study.shortTitle}</span>
        </div>

        <header className="creative-case-hero">
          <div>
            <p className="creative-eyebrow">Case study · {study.number}</p>
            {study.statusLabel && <p className="creative-project-status">{study.statusLabel}</p>}
            <h1>{study.title}</h1>
            <p>{study.discipline}</p>
          </div>
          <div className="creative-case-summary">{study.summary}</div>
        </header>

        <div className="creative-case-visual"><PortfolioVisual study={study} /></div>

        <div className="creative-case-story">
          <section><p className="creative-eyebrow">The challenge</p><h2>Make the complicated feel considered.</h2></section>
          <p>{study.challenge}</p>
          <section><p className="creative-eyebrow">The approach</p><h2>Build clarity into every handoff.</h2></section>
          <ol>{study.approach.map((item, itemIndex) => <li key={item}><span>0{itemIndex + 1}</span><p>{item}</p></li>)}</ol>
          <section><p className="creative-eyebrow">The outcome</p><h2>A useful experience, not just a polished screen.</h2></section>
          <p>{study.outcome}</p>
        </div>

        <aside className="creative-case-details">
          <div><small>CAPABILITIES</small>{study.capabilities.map((item) => <span key={item}>{item}</span>)}</div>
          {study.links?.length > 0 && (
            <div className="creative-case-links" aria-label={`${study.title} live links`}>
              {study.links.map((link) => (
                <a href={link.url} target="_blank" rel="noreferrer" key={link.url}>
                  {link.label} <ExternalLink size={16} aria-hidden="true" />
                </a>
              ))}
            </div>
          )}
        </aside>

        <Link className="creative-next-case" to={`/mycreative/${next.slug}`}>
          <span><small>NEXT CASE STUDY</small><strong>{next.title}</strong></span><ArrowUpRight size={28} />
        </Link>
      </article>
    </>
  );
}
